import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import SowSelect from '../shared/SowSelect';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/format';
import { parseRoles } from '../../utils/rbac';

const PREFILL_KEY = 'av_new_sow_prefill';
const SERVICE_TYPES = ['Leased Line', 'Support Contract', 'Licence', 'SIM', 'Warranty', 'Maintenance', 'Circuit', 'Other'];

function daysLeft(dateStr) {
  if (!dateStr) return null;
  const end = new Date(`${dateStr}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((end - today) / 86400000);
}

function ExpiryBadge({ endDate }) {
  const d = daysLeft(endDate);
  if (d == null) return null;
  const cls = d < 0 ? 'bg-red-100 text-red-700' : d <= 30 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600';
  const label = d < 0 ? `Overdue ${Math.abs(d)}d` : d === 0 ? 'Expires today' : `${d}d left`;
  return <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${cls}`}>{label}</span>;
}

// ── Create / edit a service renewal ─────────────────────────────────────────
function RenewalFormModal({ initial, sows, lockSow, onClose, onSaved }) {
  const { toast } = useToast();
  const [form, setForm] = useState(() => ({
    sow_id: initial?.sow_id || lockSow?.sow_id || '',
    sow_hardware_line_id: initial?.sow_hardware_line_id || lockSow?.sow_hardware_line_id || '',
    name: initial?.name || '',
    service_type: initial?.service_type || '',
    start_date: initial?.start_date || '',
    end_date: initial?.end_date || '',
    description: initial?.description || '',
    notes: initial?.notes || '',
  }));
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load the chosen SOW's hardware lines so a renewal can be tied to one.
  useEffect(() => {
    if (!form.sow_id) { setLines([]); return; }
    api.get(`sows/${form.sow_id}`).then(d => setLines(d?.hardware_lines || [])).catch(() => setLines([]));
  }, [form.sow_id]);

  const save = async () => {
    if (!form.sow_id) { toast.error('Pick a SOW.'); return; }
    if (!form.name.trim()) { toast.error('Name is required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), sow_hardware_line_id: form.sow_hardware_line_id || null };
      if (initial?.id) await api.put(`service-renewals/${initial.id}`, payload);
      else await api.post('service-renewals', payload);
      toast.success(initial?.id ? 'Renewal updated.' : 'Renewal added.');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Failed to save renewal');
      setSaving(false);
    }
  };

  return (
    <Modal title={initial?.id ? 'Edit service renewal' : 'New service renewal'} onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50">Cancel</button>
        <button onClick={save} disabled={saving} className="px-4 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">{saving ? 'Saving…' : 'Save renewal'}</button>
      </>
    }>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Leased line — Building A" />
          </div>
          <div>
            <label className="form-label">Type</label>
            <input className="form-input" list="svc-types" value={form.service_type} onChange={e => set('service_type', e.target.value)} placeholder="e.g. Leased Line" />
            <datalist id="svc-types">{SERVICE_TYPES.map(t => <option key={t} value={t} />)}</datalist>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Linked SOW *</label>
            {lockSow ? (
              <div className="form-input bg-gray-50 text-[10px] cursor-default">{lockSow.sow_reference || 'This SOW'}</div>
            ) : (
              <SowSelect sows={sows} value={form.sow_id || ''} onChange={id => { set('sow_id', id); set('sow_hardware_line_id', ''); }} placeholder="Select a SOW" />
            )}
          </div>
          <div>
            <label className="form-label">Hardware line <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
            <select className="form-select" value={form.sow_hardware_line_id || ''} onChange={e => set('sow_hardware_line_id', e.target.value)} disabled={!form.sow_id}>
              <option value="">— Not tied to a specific line —</option>
              {lines.map(l => <option key={l.id} value={l.id}>{l.item_description}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">Start Date</label>
            <input type="date" className="form-input" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
          </div>
          <div>
            <label className="form-label">End Date</label>
            <input type="date" className="form-input" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} />
          </div>
        </div>

        <div>
          <label className="form-label">Description</label>
          <textarea rows={2} className="form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What this service covers…" />
        </div>
        <div>
          <label className="form-label">Notes</label>
          <textarea rows={2} className="form-textarea" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any context (supplier, circuit ref, cost)…" />
        </div>
      </div>
    </Modal>
  );
}

function DecisionModal({ renewal, decision, onClose, onSaved }) {
  const { toast } = useToast();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post(`service-renewals/${renewal.id}/acknowledge`, { decision, notes: notes.trim() || null });
      onSaved(res);
    } catch (e) {
      toast.error(e.message || 'Failed to save decision');
      setSaving(false);
    }
  };

  return (
    <Modal title={decision === 'renewing' ? 'Confirm — Renewing' : 'Confirm — Not Renewing'} onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50">Cancel</button>
        <button onClick={save} disabled={saving} className={`px-3 py-1 text-[10px] font-semibold text-white rounded disabled:opacity-50 ${decision === 'renewing' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-600 hover:bg-gray-700'}`}>
          {saving ? 'Saving…' : decision === 'renewing' ? 'Confirm & create renewal SOW' : 'Confirm Not Renewing'}
        </button>
      </>
    }>
      <div className="space-y-2">
        <div className="text-[10px] text-gray-600">
          <div className="font-bold text-navy">{renewal.sow_reference} — {renewal.name}</div>
          <div className="text-gray-400">{renewal.client_name}</div>
          <div>Ends: <strong>{formatDate(renewal.end_date)}</strong></div>
        </div>
        {decision === 'renewing' && (
          <div className="text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
            Confirming will open a New Opportunity form pre-filled with this client and reference, to finalise as the renewal SOW. It's linked back to this renewal automatically.
          </div>
        )}
        <label className="block text-[8px] font-bold text-gray-500 uppercase mt-2 mb-0.5">Notes (optional)</label>
        <textarea rows={3} className="form-input text-[10px] resize-none w-full" placeholder="Any context for this decision…" value={notes} onChange={e => setNotes(e.target.value)} />
        <div className="text-[8px] text-gray-400">This decision is permanently recorded in the audit trail and SOW history.</div>
      </div>
    </Modal>
  );
}

export default function ServiceExpiryPage() {
  const { currentUser, navigate } = useApp();
  const { toast } = useToast();
  const roles = parseRoles(currentUser?.user_role);
  const canAct    = roles.some(r => ['admin', 'pmo'].includes(r));         // renew / not-renew decision
  const canManage = roles.some(r => ['admin', 'pmo', 'pm'].includes(r));   // create / edit / delete

  const [tab, setTab] = useState('pending'); // 'pending' | 'completed'
  const [rows, setRows] = useState([]);
  const [sows, setSows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionFor, setDecisionFor] = useState(null); // { renewal, decision }
  const [editing, setEditing] = useState(null);         // null | 'new' | renewal obj

  const load = () => {
    setLoading(true);
    api.get('service-renewals', { tab }).then(r => { setRows(r || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all(['Active', 'Awaiting Payment'].map(status => api.get('sows', { status }).catch(() => [])))
      .then(lists => setSows(lists.flat()));
  }, []);

  const { next30, later } = useMemo(() => {
    const next30 = [], later = [];
    for (const r of rows) {
      const d = daysLeft(r.end_date);
      (d != null && d <= 30 ? next30 : later).push(r);
    }
    const bySoonest = (a, b) => (a.end_date || '').localeCompare(b.end_date || '');
    return { next30: next30.sort(bySoonest), later: later.sort(bySoonest) };
  }, [rows]);

  const handleSaved = async (res) => {
    const { decision } = decisionFor;
    setDecisionFor(null);
    toast.success(decision === 'renewing' ? 'Marked as renewing.' : 'Marked as not renewing.');
    if (decision === 'renewing' && res?.prefill) {
      try { sessionStorage.setItem(PREFILL_KEY, JSON.stringify(res.prefill)); } catch { /* storage blocked */ }
      navigate('tracker');
      return;
    }
    load();
  };

  const del = async (r) => {
    if (!confirm(`Delete renewal "${r.name}"? This cannot be undone.`)) return;
    try { await api.delete(`service-renewals/${r.id}`); toast.success('Renewal deleted.'); load(); }
    catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const Row = ({ r }) => (
    <tr className="border-b border-gray-100 hover:bg-gold/5">
      <td className="px-2 py-1.5">
        <div className="text-[10px] font-bold text-navy">{r.name}</div>
        <button onClick={() => navigate('sow-detail', r.sow_id)} className="text-[8px] text-gray-400 hover:underline">{r.sow_reference}{r.client_name ? ` · ${r.client_name}` : ''}</button>
      </td>
      <td className="px-2 py-1.5 text-[9px] text-gray-600">{r.service_type || '—'}</td>
      <td className="px-2 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{formatDate(r.start_date) || '—'}</td>
      <td className="px-2 py-1.5 text-[9px] text-gray-700 font-semibold whitespace-nowrap">{formatDate(r.end_date)}</td>
      <td className="px-2 py-1.5"><ExpiryBadge endDate={r.end_date} /></td>
      <td className="px-2 py-1.5 text-[8px] text-gray-400">
        {r.reminder_viewed_at ? <span title={r.viewed_by_name}>Seen by {r.viewed_by_name || 'someone'}</span> : r.reminder_sent_at ? 'Reminder sent' : '—'}
      </td>
      <td className="px-2 py-1.5 text-right whitespace-nowrap">
        {canAct && (
          <>
            <button onClick={() => setDecisionFor({ renewal: r, decision: 'renewing' })} className="px-2 py-0.5 text-[8px] font-bold rounded bg-emerald-600 text-white hover:bg-emerald-700 mr-1">Renewing</button>
            <button onClick={() => setDecisionFor({ renewal: r, decision: 'not_renewing' })} className="px-2 py-0.5 text-[8px] font-bold rounded border border-gray-300 text-gray-600 hover:bg-gray-50 mr-1">Not Renewing</button>
          </>
        )}
        {canManage && (
          <>
            <button onClick={() => setEditing(r)} className="text-[8px] text-gray-400 hover:text-navy mr-1">Edit</button>
            <button onClick={() => del(r)} className="text-[8px] text-gray-400 hover:text-red-500">Delete</button>
          </>
        )}
        {!canAct && !canManage && <span className="text-[8px] text-gray-400">View only</span>}
      </td>
    </tr>
  );

  const CompletedRow = ({ r }) => (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-1.5">
        <div className="text-[10px] font-bold text-navy">{r.name}</div>
        <button onClick={() => navigate('sow-detail', r.sow_id)} className="text-[8px] text-gray-400 hover:underline">{r.sow_reference}{r.client_name ? ` · ${r.client_name}` : ''}</button>
      </td>
      <td className="px-2 py-1.5 text-[9px] text-gray-600">{r.service_type || '—'}</td>
      <td className="px-2 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{formatDate(r.end_date)}</td>
      <td className="px-2 py-1.5">
        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${r.renewal_status === 'renewing' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
          {r.renewal_status === 'renewing' ? 'Renewing' : 'Not Renewing'}
        </span>
        {r.new_sow_reference && (
          <button onClick={() => navigate('sow-detail', r.new_sow_id)} className="ml-1.5 text-[8px] text-navy hover:underline font-mono">→ {r.new_sow_reference}</button>
        )}
      </td>
      <td className="px-2 py-1.5 text-[9px] text-gray-600">{r.decided_by_name || '—'}<div className="text-[8px] text-gray-400">{formatDate(r.renewal_decided_at)}</div></td>
      <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[240px] truncate" title={r.notes || ''}>{r.notes || '—'}</td>
    </tr>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-bold text-navy">Service Renewals</h2>
          <div className="text-[9px] text-gray-500">Recurring / expiring services (leased lines, support contracts, etc.) across all live SOWs. A SOW line can carry several renewals — each is tracked and acknowledged on its own end date.</div>
        </div>
        {canManage && (
          <button onClick={() => setEditing('new')} className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ New renewal</button>
        )}
      </div>

      <div className="flex gap-1 mb-2">
        <button onClick={() => setTab('pending')} className={`px-3 py-1 text-[9px] font-bold rounded-t border-b-2 ${tab === 'pending' ? 'border-gold text-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>Needs Decision</button>
        <button onClick={() => setTab('completed')} className={`px-3 py-1 text-[9px] font-bold rounded-t border-b-2 ${tab === 'completed' ? 'border-gold text-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>Completed</button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
      ) : tab === 'pending' ? (
        <div className="space-y-4">
          {[['Next 30 Days', next30, 'text-amber-700', 'Nothing due in the next 30 days.'], ['Later', later, 'text-gray-500', 'Nothing else pending.']].map(([title, list, cls, empty]) => (
            <div key={title}>
              <div className={`text-[9px] font-bold uppercase mb-1 ${cls}`}>{title} ({list.length})</div>
              <div className="bg-white border border-gray-200 rounded overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    {['Service', 'Type', 'Starts', 'Ends', '', 'Seen', ''].map((h, i) => (
                      <th key={i} className="px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {list.map(r => <Row key={r.id} r={r} />)}
                    {list.length === 0 && <tr><td colSpan={7} className="text-center py-4 text-[9px] text-gray-400">{empty}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              {['Service', 'Type', 'Ended', 'Decision', 'Decided By', 'Notes'].map(h => (
                <th key={h} className="px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 text-left">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map(r => <CompletedRow key={r.id} r={r} />)}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[10px] text-gray-400">No acknowledged renewals yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {decisionFor && (
        <DecisionModal renewal={decisionFor.renewal} decision={decisionFor.decision} onClose={() => setDecisionFor(null)} onSaved={handleSaved} />
      )}
      {editing && (
        <RenewalFormModal
          initial={editing === 'new' ? null : editing}
          sows={sows}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
